import { useDropdownMenu } from '@lwt-helix/useDropdownMenu';
import React, { useRef, useContext, useState, useEffect } from 'react';
import DropdownMenu from './DropdownMenu.js';
import { StyledNavbarNav } from './Navbar.styles.js';
import NavbarBadge from './NavbarBadge.js';
import { NavbarContext } from './NavbarContext.js';
import NavbarDropdownNavItem from './NavbarDropdownNavItem.js';
import { NavbarHelixIcon } from './NavbarHelixIcon.js';
import { checkIfChildIsA, getTarget, clickDropdownItem } from './NavbarHelpers.js';
import NavbarMenuItem from './NavbarMenuItem.js';

/* eslint-disable max-len */
const NavbarNavigationBar = ({ moreLabel = 'More', navItems = [], location = { pathname: '' }, hideMoreDropdownArrow = false, dataLwtId = 'navbar-navigation-bar', className, linkWrapper, dropdownsExpandedByDefault = false }) => {
    const navigationBarRef = useRef({});
    const overflowMenuButton = useRef(null);
    const itemsWrapperRef = useRef({});
    const moreButtonWrapperRef = useRef({});
    const { isDark, setNavItems, setLocation, setNavbarLinkWrapper, setDropdownsExpandedByDefault, hasAdditionalNavItems } = useContext(NavbarContext);
    const [collections, setCollections] = useState({
        items: navItems,
        overflowItems: []
    });
    const menuRef = useRef(null);
    const lastItemWidth = useRef(undefined);
    const totalItemsWidth = useRef(0);
    // flat list of menu items needed for downshift. this same list is needed when selecting by `highlightedIndex`
    const getFlattenedOverflowItems = () => collections.overflowItems.some((item) => item === null || item === void 0 ? void 0 : item.menuItems)
        ? collections.overflowItems.reduce((acc, val) => {
            acc.push(val);
            if (val === null || val === void 0 ? void 0 : val.menuItems) {
                acc.push(...val.menuItems);
            }
            return acc;
        }, [])
        : collections.overflowItems;
    const { isOpen, getToggleButtonProps, getMenuProps, highlightedIndex, getItemProps, setReferenceElement, setPopperElement, attributes, styles } = useDropdownMenu({
        placement: 'bottom-end',
        items: getFlattenedOverflowItems(),
        itemToString: (item) => (item.label ? item.label : ''),
        resetSelectedItemOnClose: true,
        offset: [0, 8]
    });
    const shiftIntoOverflowItems = () => {
        var _a;
        lastItemWidth.current = getFullWidth((_a = itemsWrapperRef === null || itemsWrapperRef === void 0 ? void 0 : itemsWrapperRef.current) === null || _a === void 0 ? void 0 : _a.lastElementChild);
        const newColl = copyCollections();
        newColl.overflowItems.unshift(newColl.items[newColl.items.length - 1]);
        newColl.items.pop();
        setCollections(newColl);
    };
    const shiftIntoItems = () => {
        var _a;
        const newColl = copyCollections();
        newColl.items.push(newColl.overflowItems[0]);
        newColl.overflowItems.shift();
        setCollections(newColl);
        lastItemWidth.current = getFullWidth((_a = itemsWrapperRef === null || itemsWrapperRef === void 0 ? void 0 : itemsWrapperRef.current) === null || _a === void 0 ? void 0 : _a.lastElementChild);
    };
    const copyCollections = () => {
        return { items: [...collections.items], overflowItems: [...collections.overflowItems] };
    };
    const isOverflowing = () => navigationBarRef.current && navigationBarRef.current.offsetWidth < navigationBarRef.current.scrollWidth;
    const isUnderflowing = () => {
        var _a;
        return navigationBarRef.current &&
            totalItemsWidth.current + ((_a = lastItemWidth === null || lastItemWidth === void 0 ? void 0 : lastItemWidth.current) !== null && _a !== void 0 ? _a : 0) < navigationBarRef.current.scrollWidth;
    };
    const updateItems = () => {
        if (!(navigationBarRef === null || navigationBarRef === void 0 ? void 0 : navigationBarRef.current) || (lastItemWidth === null || lastItemWidth === void 0 ? void 0 : lastItemWidth.current) === 0) {
            return;
        }
        if ((collections === null || collections === void 0 ? void 0 : collections.items.length) > 0 && isOverflowing()) {
            shiftIntoOverflowItems();
        }
        else if ((collections === null || collections === void 0 ? void 0 : collections.overflowItems.length) > 0 && isUnderflowing()) {
            shiftIntoItems();
        }
    };
    const handleDropdownToggleKeydown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
            e.preventDefault();
            const menuItem = getFlattenedOverflowItems()[highlightedIndex];
            menuItem.toggleDropdown = !menuItem.toggleDropdown;
        }
        clickDropdownItem(e, menuRef);
    };
    const navItemProps = (navItem) => {
        return {
            disabled: navItem.disabled,
            className: navItem.url === location.pathname || navItem.active || navItem.activePathname === location.pathname
                ? 'helix-unified-nav__navigation-item active'
                : 'helix-unified-nav__navigation-item'
        };
    };
    const linkWrapperElement = (navItem) => {
        var _a, _b;
        return linkWrapper ? ((_b = linkWrapper({
            item: navItem,
            props: Object.assign(Object.assign({}, (_a = linkWrapper({
                item: navItem,
                props: navItemProps(navItem)
            })) === null || _a === void 0 ? void 0 : _a.props), { disabled: navItem.disabled, className: navItem.url === location.pathname ||
                    navItem.active ||
                    navItem.activePathname === location.pathname
                    ? 'helix-unified-nav__navigation-item active'
                    : 'helix-unified-nav__navigation-item' })
        })) === null || _b === void 0 ? void 0 : _b.props.children) : (React.createElement(React.Fragment, null));
    };
    const getFullWidth = (element) => {
        var _a;
        if (!element || element === null)
            return undefined;
        const laststyle = window.getComputedStyle(element);
        const margin = parseFloat(laststyle.marginLeft) + parseFloat(laststyle.marginRight);
        const width = ((_a = element.offsetWidth) !== null && _a !== void 0 ? _a : 0) + margin;
        return isNaN(width) ? 0 : width;
    };
    useEffect(() => {
        setNavItems([...navItems]);
        setCollections({
            items: navItems,
            overflowItems: []
        });
    }, [navItems]);
    useEffect(() => {
        setLocation(location.pathname);
    }, [location]);
    useEffect(() => {
        if (linkWrapper) {
            setNavbarLinkWrapper(() => linkWrapper);
        }
    }, [linkWrapper]);
    useEffect(() => {
        setDropdownsExpandedByDefault(dropdownsExpandedByDefault);
    }, [dropdownsExpandedByDefault]);
    useEffect(() => {
        var _a;
        const moreButtonSize = (_a = getFullWidth(moreButtonWrapperRef.current)) !== null && _a !== void 0 ? _a : 0;
        totalItemsWidth.current =
            Array.from(itemsWrapperRef.current.children)
                .map((e) => { var _a; return (_a = getFullWidth(e)) !== null && _a !== void 0 ? _a : 0; })
                .reduce((acc, curr) => acc + curr, 0) + moreButtonSize;
        updateItems();
        // Limiting the rate of update calls to every 25ms
        // until no resizing has occurred after 100ms
        let timeout;
        let interval = undefined;
        const listener = () => {
            // reset timer since window is still resizing
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                // no resizing in the last 100ms so stop periodic update
                clearInterval(interval);
                interval = undefined;
                updateItems();
            }, 100);
            // call update every 25ms if not started
            !interval && (interval = setInterval(() => updateItems(), 25));
        };
        window.addEventListener('resize', listener);
        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
            window.removeEventListener('resize', listener);
        };
    }, [collections]);
    return (React.createElement(StyledNavbarNav, { ref: navigationBarRef, "$isDark": isDark, "$hasAdditionalNavItems": hasAdditionalNavItems, "$hasOverflowItems": collections.overflowItems.length > 0, className: `helix-unified-nav__navigation ${className ? className : ''}`, "data-lwt-id": dataLwtId },
        React.createElement("div", { ref: itemsWrapperRef, className: 'helix-unified-nav__navigation-items-wrapper' }, collections.items.map((navItem) => {
            var _a, _b;
            return navItem && (React.createElement(React.Fragment, { key: `nav-bar-item-${navItem.label}` }, navItem.menuItems ? (React.createElement(NavbarDropdownNavItem, { location: location, linkWrapper: linkWrapper, navItem: navItem })) : (React.createElement(React.Fragment, { key: `nav-bar-item-${navItem.label}` }, navItem.onClick ? (React.createElement("button", { className: navItem.url === location.pathname ||
                    navItem.active ||
                    navItem.activePathname === location.pathname
                    ? 'helix-unified-nav__navigation-item active'
                    : 'helix-unified-nav__navigation-item', disabled: navItem.disabled, onClick: (e) => navItem.onClick && navItem.onClick(e) },
                navItem.label,
                navItem.badge &&
                    navItem.badge.label !== undefined &&
                    !navItem.badge.hidden && (React.createElement(NavbarBadge, { isDark: isDark }, navItem.badge.label)))) : (React.createElement(React.Fragment, null,
                React.createElement(React.Fragment, null, linkWrapper ? (React.cloneElement(linkWrapper({
                    item: navItem,
                    props: {
                        disabled: navItem.disabled,
                        className: navItem.url === location.pathname ||
                            navItem.active ||
                            navItem.activePathname === location.pathname
                            ? 'helix-unified-nav__navigation-item active'
                            : 'helix-unified-nav__navigation-item'
                    }
                }), {
                    disabled: navItem.disabled,
                    className: navItem.url === location.pathname ||
                        navItem.active ||
                        navItem.activePathname === location.pathname
                        ? 'helix-unified-nav__navigation-item active'
                        : 'helix-unified-nav__navigation-item',
                    children: checkIfChildIsA(linkWrapper, navItem) ? (React.cloneElement(linkWrapperElement(navItem), {
                        children: (React.createElement(React.Fragment, null, (_b = linkWrapper({
                            item: navItem,
                            props: Object.assign({}, (_a = linkWrapper({
                                item: navItem,
                                props: navItemProps(navItem)
                            })) === null || _a === void 0 ? void 0 : _a.props)
                        })) === null || _b === void 0 ? void 0 :
                            _b.props.children.props.children,
                            navItem.badge &&
                                navItem.badge.label !== undefined &&
                                !navItem.badge.hidden && (React.createElement(NavbarBadge, null, navItem.badge.label))))
                    })) : (React.createElement(React.Fragment, null,
                        linkWrapperElement(navItem),
                        navItem.badge &&
                            navItem.badge.label !== undefined &&
                            !navItem.badge.hidden && (React.createElement(NavbarBadge, null, navItem.badge.label))))
                })) : (React.createElement("a", Object.assign({ href: navItem.url, key: `nav-bar-item-${navItem.label}`, className: navItem.url === location.pathname ||
                        navItem.active ||
                        navItem.activePathname === location.pathname
                        ? `helix-unified-nav__navigation-item active ${navItem.disabled ? 'disabled' : ''}`
                        : `helix-unified-nav__navigation-item ${navItem.disabled ? 'disabled' : ''}`, onClick: (e) => {
                        if (navItem.disabled) {
                            e.preventDefault();
                            return;
                        }
                        navItem.onClick && navItem.onClick(e);
                    } }, getTarget(navItem)),
                    navItem.label,
                    navItem.badge &&
                        navItem.badge.label &&
                        !navItem.badge.hidden && (React.createElement(NavbarBadge, { isDark: isDark }, navItem.badge.label)))))))))));
        })),
        React.createElement("div", { className: 'helix-unified-nav__overflow-button-wrapper', ref: (ref) => {
                moreButtonWrapperRef.current = ref;
                setReferenceElement(ref);
            } },
            React.createElement("button", Object.assign({ className: 'helix-unified-nav__navigation-item helix-unified-nav__navigation-item_more' }, getToggleButtonProps({
                ref: overflowMenuButton
            }, { suppressRefError: true })),
                moreLabel,
                !hideMoreDropdownArrow && React.createElement(NavbarHelixIcon, { iconName: 'dropdown_open' }))),
        React.createElement(DropdownMenu, Object.assign({ inNavbar: true, maxWidth: '300px', ref: setPopperElement, isOpen: isOpen && collections.overflowItems.length > 0, className: 'helix-unified-nav__overflow-dropdown-menu', style: styles.popper }, attributes.popper),
            React.createElement("ul", Object.assign({}, getMenuProps({
                onKeyDown: (e) => handleDropdownToggleKeydown(e),
                ref: (ref) => (menuRef.current = ref)
            }, { suppressRefError: true })), isOpen &&
                collections.overflowItems.map((overflowItem, index) => (React.createElement(NavbarMenuItem, { linkWrapper: linkWrapper, key: `${overflowItem.label}${index}`, menuItem: overflowItem, highlightedIndex: highlightedIndex, index: index +
                        collections.overflowItems
                            .filter((overflowItem, oIndex) => overflowItem.menuItems && oIndex < index)
                            .reduce((acc, cur) => { var _a, _b; return acc + ((_b = (_a = cur.menuItems) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0); }, 0), location: location, getItemProps: getItemProps })))))));
};
NavbarNavigationBar.displayName = 'NavbarNavigationBar';

export { NavbarNavigationBar as default };
//# sourceMappingURL=NavbarNavigationBar.js.map
